import React, { useContext, useMemo, useRef } from "react";
import * as RechartsPrimitive from "recharts";

// Format: { THEME_NAME: CSS_SELECTOR }
const THEMES = { light: "", dark: ".dark" };

const ChartContext = React.createContext(null);

function useChart() {
  const context = useContext(ChartContext);
  if (!context) {
    throw new Error("useChart must be used within a <ChartContainer />");
  }
  return context;
}

function ChartContainer({ id, className = "", children, config, ...props }) {
  const uniqueId = useRef(`chart-${Math.random().toString(36).substr(2, 9)}`);
  const chartId = `chart-${id || uniqueId.current}`;

  const styleString = generateChartStyle(chartId, config);

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-slot="chart"
        data-chart={chartId}
        className={`chart-container ${className}`}
        {...props}
      >
        <style dangerouslySetInnerHTML={{ __html: styleString }} />
        <RechartsPrimitive.ResponsiveContainer>
          {children}
        </RechartsPrimitive.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
}

function generateChartStyle(id, config) {
  const colorConfig = Object.entries(config || {}).filter(
    ([, c]) => c.theme || c.color
  );

  if (!colorConfig.length) return "";

  return Object.entries(THEMES)
    .map(([theme, prefix]) => {
      const rules = colorConfig
        .map(([key, itemConfig]) => {
          const color =
            (itemConfig.theme && itemConfig.theme[theme]) || itemConfig.color;
          return color ? `--color-${key}: ${color};` : null;
        })
        .filter(Boolean)
        .join("\n");

      return `${prefix} [data-chart=${id}] {${rules}}`;
    })
    .join("\n");
}

const ChartTooltip = RechartsPrimitive.Tooltip;

function ChartTooltipContent({
  active,
  payload,
  className = "",
  indicator = "dot",
  hideLabel = false,
  hideIndicator = false,
  label,
  labelFormatter,
  labelClassName = "",
  formatter,
  color,
  nameKey,
  labelKey,
}) {
  const { config } = useChart();

  const tooltipLabel = useMemo(() => {
    if (hideLabel || !payload?.length) return null;

    const [item] = payload;
    const key = labelKey || item?.dataKey || item?.name || "value";
    const itemConfig = getPayloadConfigFromPayload(config, item, key);
    const value =
      !labelKey && typeof label === "string"
        ? config[label]?.label || label
        : itemConfig?.label;

    if (!value) return null;

    if (labelFormatter) {
      return (
        <div className={`chart-tooltip-label ${labelClassName}`}>
          {labelFormatter(value, payload)}
        </div>
      );
    }

    return (
      <div className={`chart-tooltip-label ${labelClassName}`}>{value}</div>
    );
  }, [label, labelFormatter, payload, hideLabel, labelClassName, config, labelKey]);

  if (!active || !payload?.length) return null;

  const nestLabel = payload.length === 1 && indicator !== "dot";

  return (
    <div className={`chart-tooltip ${className}`}>
      {!nestLabel ? tooltipLabel : null}
      <div className="chart-tooltip-items">
        {payload.map((item, index) => {
          const key = nameKey || item.name || item.dataKey || "value";
          const itemConfig = getPayloadConfigFromPayload(config, item, key);
          const indicatorColor = color || item.payload.fill || item.color;

          return (
            <div
              key={item.dataKey}
              className={`chart-tooltip-item ${
                indicator === "dot" ? "with-dot" : ""
              }`}
            >
              {formatter && item?.value !== undefined && item.name ? (
                formatter(item.value, item.name, item, index, item.payload)
              ) : (
                <>
                  {!hideIndicator && (
                    <div
                      className={`chart-tooltip-indicator indicator-${indicator}`}
                      style={{
                        "--color-bg": indicatorColor,
                        "--color-border": indicatorColor,
                      }}
                    />
                  )}
                  <div
                    className={`chart-tooltip-content ${
                      nestLabel ? "with-nest" : ""
                    }`}
                  >
                    <div className="chart-tooltip-label-wrapper">
                      {nestLabel ? tooltipLabel : null}
                      <span className="chart-tooltip-label-text">
                        {itemConfig?.label || item.name}
                      </span>
                    </div>
                    {item.value && (
                      <span className="chart-tooltip-value">
                        {item.value.toLocaleString()}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const ChartLegend = RechartsPrimitive.Legend;

function ChartLegendContent({
  className = "",
  hideIcon = false,
  payload,
  verticalAlign = "bottom",
  nameKey,
}) {
  const { config } = useChart();

  if (!payload?.length) return null;

  return (
    <div
      className={`chart-legend ${
        verticalAlign === "top" ? "legend-top" : "legend-bottom"
      } ${className}`}
    >
      {payload.map((item) => {
        const key = nameKey || item.dataKey || "value";
        const itemConfig = getPayloadConfigFromPayload(config, item, key);

        return (
          <div key={item.value} className="chart-legend-item">
            {!hideIcon && (
              <div
                className="chart-legend-icon"
                style={{ backgroundColor: item.color }}
              />
            )}
            {itemConfig?.label}
          </div>
        );
      })}
    </div>
  );
}

// Helper
function getPayloadConfigFromPayload(config, payload, key) {
  if (!payload || typeof payload !== "object") return undefined;
  const payloadPayload =
    payload.payload && typeof payload.payload === "object"
      ? payload.payload
      : undefined;

  let configLabelKey = key;

  if (key in payload && typeof payload[key] === "string")
    configLabelKey = payload[key];
  else if (
    payloadPayload &&
    key in payloadPayload &&
    typeof payloadPayload[key] === "string"
  )
    configLabelKey = payloadPayload[key];

  return configLabelKey in config ? config[configLabelKey] : config[key];
}

export {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  generateChartStyle as ChartStyle,
};